import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  getOnboardingUnlockedDestinations,
  hasOnboardingReactionPlayed,
  isOnboardingActive,
  isOnboardingIntro,
  isOnboardingTour,
  type OnboardingDestination,
  type OnboardingState,
} from "@-/dev.improve.haniverse.lib.onboarding.state";
import { useEffect } from "react";
import { playOnboardingReaction } from "#onboarding-store";
import { useOnboardingLearningProgress } from "#use-onboarding-learning-progress";

/** The device's onboarding, live; `null` when there is none. */
export function useOnboardingState(): OnboardingState | null {
  const [state] = useDeviceSetting("onboarding");
  return state;
}

/** Whether the onboarding still shapes the app. */
export function useIsOnboardingActive(): boolean {
  return isOnboardingActive(useOnboardingState());
}

/** Whether the globe and its questions still cover the app (`isOnboardingIntro`). */
export function useIsOnboardingIntro(): boolean {
  return isOnboardingIntro(useOnboardingState());
}

/** Whether the onboarding is still touring the app (`isOnboardingTour`). */
export function useIsOnboardingTour(): boolean {
  return isOnboardingTour(useOnboardingState());
}

/**
 * The strip destinations the onboarding has unlocked so far, or `null` when it does not limit the
 * strip (no onboarding, or a finished one).
 */
export function useOnboardingUnlockedDestinations(): readonly OnboardingDestination[] | null {
  return getOnboardingUnlockedDestinations(useOnboardingState());
}

/**
 * Plays the reactions to what the learner learns, wherever they learned it. Mount once, in the
 * app shell:
 *
 * - An account that turns out to have learned before (one that signed up again through "Get
 *   Started") skips the onboarding altogether.
 * - The first character learned unlocks Stats, which the sheet then announces. It stays unlocked
 *   even if that character is undone.
 * - The first level learned finishes the onboarding, which hands the learner their own strip.
 */
export function useOnboardingProgressSync(): void {
  const state = useOnboardingState();
  const progress = useOnboardingLearningProgress({ enabled: isOnboardingActive(state) });
  const isIntro = isOnboardingIntro(state);
  const isActive = isOnboardingActive(state);
  const hasPlayedFirstCharacter =
    state !== null && hasOnboardingReactionPlayed(state, "first-character");
  const hasLearnedCharacter = progress?.hasLearnedCharacter ?? null;
  const hasLearnedFirstLevel = progress?.hasLearnedFirstLevel ?? null;
  useEffect(() => {
    if (!isActive || hasLearnedCharacter === null) {
      return;
    }
    if (hasLearnedFirstLevel === true || (isIntro && hasLearnedCharacter)) {
      playOnboardingReaction("done");
    } else if (hasLearnedCharacter && !hasPlayedFirstCharacter) {
      playOnboardingReaction("first-character");
    }
  }, [hasLearnedCharacter, hasLearnedFirstLevel, hasPlayedFirstCharacter, isActive, isIntro]);
}
